import { AppArea, ROUTE_PATH } from "../constants/routing";
import { tenantService } from "../services/tenant";
import { assertNever } from "../ts/typeHelpers";
import { UrlHelper } from "./url";

export class RoutingHelper {
  /** Resolves app area from current URL.
   *  E.g. https://example.com/tenantId/admin -> admin.
   *  E.g. https://example.com/admin -> admin.
   */
  public static resolveAppArea(params?: { url?: string; pathname?: string }): AppArea | null {
    const url = params?.url || window.location.href;
    let pathname = params?.pathname || UrlHelper.getUrlPathname(url);
    const tenantInfo = tenantService.resolveTenantFromBasePath({ url: url });
    if (tenantInfo) {
      pathname = pathname.replace(`/${tenantInfo.identifier}`, ""); // exclude tenant identifier
    }
    const parts = pathname.split("/").filter((x) => !!x);
    const area = parts[0] || null;
    return this.isAppArea(area) ? area : null;
  }

  /** Redirects to new app area URL with full page reload.
   *  E.g. https://example.com/admin/a/b/c -> https://example.com/app
   */
  public static redirectToAppArea(params: { newArea: AppArea; newPath?: string }): void {
    const currentArea = this.resolveAppArea();
    if (currentArea !== params.newArea) {
      const tenantInfo = tenantService.resolveTenantFromBasePath();
      const url = window.location.origin; // drop pathname and query string
      const newPathname =
        "/" +
        [
          ...(tenantInfo ? [tenantInfo.identifier] : []),
          params.newArea.toString(),
          params.newPath?.replace(/^\//, ""),
        ]
          .filter(Boolean)
          .join("/");
      const newUrl = UrlHelper.updateUrlPathname(url, newPathname);
      console.log("Redirect to app area:", params.newArea);
      UrlHelper.redirectToUrl(newUrl);
    }
  }

  public static isAppArea(value: Nil<string>): value is AppArea {
    return !!value && Object.values(AppArea).includes(value as AppArea);
  }

  public static isAppAreaActive(params: {
    area: AppArea;
    url?: string;
    pathname?: string;
  }): boolean {
    return this.resolveAppArea(params) === params.area;
  }

  public static isFleetAreaActive(params?: { url?: string; pathname?: string }): boolean {
    return this.resolveAppArea(params) === AppArea.Fleet;
  }

  public static getCurrentAppAreaRootPathOrDefault(): string {
    const area = this.resolveAppArea();
    const defaultPath = ROUTE_PATH.ROOT;
    if (!area) {
      return defaultPath;
    }
    switch (area) {
      case AppArea.Development:
        return ROUTE_PATH.APP_AREA_DEV;
      case AppArea.Auth:
        return ROUTE_PATH.APP_AREA_AUTH;
      case AppArea.Fleet:
        return ROUTE_PATH.APP_AREA_FLEET_APP;
      case AppArea.FleetCustomer:
        return ROUTE_PATH.APP_AREA_FLEET_CUSTOMER;
      case AppArea.Admin:
        return ROUTE_PATH.APP_AREA_ADMIN;
      default:
        assertNever(area);
        return defaultPath;
    }
  }
}
